import React, { useState, useEffect } from 'react';
import {
  Modal,
  Select,
  TextField,
  Button,
  Banner,
  Text,
  Card
} from '@shopify/polaris';
import { ResourcePicker, Provider } from '@shopify/app-bridge-react';
import { apiService } from '../services/api';
import { useShopify } from '../contexts/ShopifyContext';

const AVAILABLE_LANGUAGES = [
  { label: 'English', value: 'en' },
  { label: 'French', value: 'fr' },
  { label: 'German', value: 'de' },
  { label: 'Spanish', value: 'es' },
  { label: 'Italian', value: 'it' },
  { label: 'Portuguese', value: 'pt' },
  { label: 'Dutch', value: 'nl' },
  { label: 'Polish', value: 'pl' },
  { label: 'Swedish', value: 'sv' },
  { label: 'Finnish', value: 'fi' },
  { label: 'Danish', value: 'da' },
  { label: 'Norwegian', value: 'no' }
];

const getLanguageLabel = (value: string) => {
  const language = AVAILABLE_LANGUAGES.find(lang => lang.value === value);
  return language ? language.label : value;
};

interface TestPromptModalProps {
  open: boolean;
  onClose: () => void;
  currentPrompt: string;
  targetLanguages: string[]; // Language codes (e.g., 'es', 'fr', 'de')
  baseLanguage: string;
}

export const TestPromptModal: React.FC<TestPromptModalProps> = ({
  open,
  onClose,
  currentPrompt,
  targetLanguages,
  baseLanguage
}) => {

  const [testMode, setTestMode] = useState<'text' | 'resource'>('text');
  const [customText, setCustomText] = useState('');
  
  // Fix: Ensure selectedLanguage always has a valid value
  const getInitialLanguage = () => {
    if (targetLanguages && targetLanguages.length > 0) {
      return targetLanguages[0];
    }
    // Fallback to a default language if targetLanguages is empty
    return 'es'; // Default to Spanish
  };
  
  const [selectedLanguage, setSelectedLanguage] = useState(getInitialLanguage());
  const [selectedResourceType, setSelectedResourceType] = useState('PRODUCT');
  const [selectedResourceId, setSelectedResourceId] = useState('');
  const [selectedResourceTitle, setSelectedResourceTitle] = useState('');
  const [result, setResult] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResourcePicker, setShowResourcePicker] = useState(false);

  const { app: appBridge } = useShopify();

  const resourceTypes = [
    { label: 'Products', value: 'PRODUCT' },
    { label: 'Collections', value: 'COLLECTION' },
  ];

  // Map resource types to Shopify ResourcePicker types
  const getResourcePickerType = (type: string): 'Product' | 'Collection' => {
    switch (type) {
      case 'PRODUCT':
        return 'Product';
      case 'COLLECTION':
        return 'Collection';
      default:
        return 'Product';
    }
  };

  const handleResourcePickerSelection = (resources: any) => {
    if (resources.selection && resources.selection.length > 0) {
      const selectedResource = resources.selection[0];
      setSelectedResourceId(selectedResource.id);
      setSelectedResourceTitle(selectedResource.title);
    }
    setShowResourcePicker(false);
  };

  const handleSelectResource = () => {
    const hostValue = sessionStorage.getItem('shopify_host');
    
    if (!appBridge) {
      console.error('[ERROR] AppBridge not available for ResourcePicker');
      return;
    }
    
    if (!hostValue) {
      console.error('[ERROR] No host value found in sessionStorage');
      return;
    }
    
    setShowResourcePicker(true);
  };

  const handleTestPrompt = async () => {
    if (!currentPrompt.trim()) {
      setError('Please enter a translation prompt');
      return;
    }

    if (testMode === 'text' && !customText.trim()) {
      setError('Please enter text to translate');
      return;
    }

    if (testMode === 'resource' && !selectedResourceId) {
      setError('Please select a resource to test');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const testData: any = {
        prompt: currentPrompt,
        targetLanguage: selectedLanguage,
      };

      if (testMode === 'text') {
        testData.text = customText;
      } else {
        testData.resourceType = selectedResourceType;
        testData.resourceId = selectedResourceId;
      }

      const response = await apiService.testPrompt(testData);

      if (response.error) {
        setError(response.error);
      } else if (response.data) {
        // Set the result with proper language information
        setResult({
          originalText: response.data.originalText,
          translatedText: response.data.translatedText,
          targetLanguage: getLanguageLabel(response.data.targetLanguage),
          sourceLanguage: getLanguageLabel(baseLanguage)
        });
      }
    } catch (error) {
      console.error('Test prompt error:', error);
      setError('Failed to test prompt. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setTestMode('text');
    setCustomText('');
    setSelectedLanguage(getInitialLanguage());
    setSelectedResourceType('PRODUCT');
    setSelectedResourceId('');
    setSelectedResourceTitle('');
    setShowResourcePicker(false);

    setResult(null);
    setError(null);
    onClose();
  };

  const renderResourceSelection = () => {
    if (selectedResourceId) {
      return (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Text variant="bodyMd" as="p">
                <strong>{selectedResourceTitle}</strong>
              </Text>
              <Text variant="bodySm" as="p">
                {resourceTypes.find(r => r.value === selectedResourceType)?.label} • {selectedResourceId}
              </Text>
            </div>
            <Button
              onClick={() => {
                setSelectedResourceId('');
                setSelectedResourceTitle('');
              }}
              size="slim"
              tone="critical"
            >
              Change
            </Button>
          </div>
        </Card>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                 <Button
           onClick={handleSelectResource}
           size="large"
           fullWidth
         >
           Select {resourceTypes.find(r => r.value === selectedResourceType)?.label || 'Resource'}
         </Button>
        
        {!appBridge && (
          <Banner tone="warning">
            <p>Shopify App Bridge is not available. Please refresh the page and try again.</p>
          </Banner>
        )}
      </div>
    );
  };

  // Check sessionStorage values on mount and when modal opens
  useEffect(() => {
    if (open) {
      const checkSessionStorage = () => {
        const hostValue = sessionStorage.getItem('shopify_host');
        const shopValue = sessionStorage.getItem('shopify_shop');
        
        if (hostValue && shopValue) {
          // setSessionStorageReady(true); // This line is removed
        } else {
          // Retry after a short delay
          setTimeout(checkSessionStorage, 100);
        }
      };
      
      checkSessionStorage();
    }
  }, [open]);

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title="Test Translation Prompt"
        primaryAction={{
          content: 'Test Prompt',
          onAction: handleTestPrompt,
          loading: isLoading,
          disabled: isLoading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleClose,
          },
        ]}
      >
        <Modal.Section>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Test Mode Selection */}
            <div>
              <Text variant="bodyMd" as="p" fontWeight="semibold">
                Test Mode
              </Text>
              <Select
                label=""
                options={[
                  { label: 'Custom Text', value: 'text' },
                  { label: 'Store Resource', value: 'resource' }
                ]}
                value={testMode}
                onChange={(value) => setTestMode(value as 'text' | 'resource')}
              />
            </div>

            {/* Target Language Selection */}
            <div>
              <Text variant="bodyMd" as="p" fontWeight="semibold">
                Target Language
              </Text>
              <Select
                label=""
                options={AVAILABLE_LANGUAGES.filter(lang => targetLanguages.includes(lang.value)).map(lang => ({ label: lang.label, value: lang.value }))}
                value={selectedLanguage}
                onChange={(value) => setSelectedLanguage(value)}
              />
            </div>

            {/* Test Content Section */}
            {testMode === 'text' ? (
              <TextField
                label="Text to Translate"
                value={customText}
                onChange={setCustomText}
                multiline={8}
                placeholder="Enter the text you want to translate..."
                helpText="This text will be translated using your current prompt"
                autoComplete="off"
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Resource Type Selection */}
                <div>
                  <Text variant="bodyMd" as="p" fontWeight="semibold">
                    Resource Type
                  </Text>
                  <Select
                    label=""
                    options={resourceTypes}
                    value={selectedResourceType}
                    onChange={(value) => {
                      setSelectedResourceType(value);
                      setSelectedResourceId('');
                      setSelectedResourceTitle('');
                    }}
                  />
                </div>

                {/* Resource Selection */}
                <div>
                  <Text variant="bodyMd" as="p" fontWeight="semibold">
                    Select Resource
                  </Text>
                  {renderResourceSelection()}
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <Banner tone="critical">
                <p>{error}</p>
              </Banner>
            )}

            {/* Results Display */}
            {result && (
              <Card>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Text variant="headingMd" as="h3">
                    Translation Results
                  </Text>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <Text variant="bodyMd" as="span">
                      <strong>{result.sourceLanguage}</strong>
                    </Text>
                    <Text variant="bodyMd" as="span">
                      →
                    </Text>
                    <Text variant="bodyMd" as="span">
                      <strong>{result.targetLanguage}</strong>
                    </Text>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Text variant="bodyMd" as="h4">
                      Original Text ({result.sourceLanguage}):
                    </Text>
                    <Card>
                      <Text as="p" variant="bodyMd">
                        {result.originalText}
                      </Text>
                    </Card>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Text variant="bodyMd" as="h4">
                      Translated Text ({result.targetLanguage}):
                    </Text>
                    <Card>
                      <Text as="p" variant="bodyMd">
                        {result.translatedText}
                      </Text>
                    </Card>
                  </div>
                </div>
              </Card>
            )}

            {/* Current Prompt Display */}
            <Card>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Text variant="headingMd" as="h3">
                  Current Prompt
                </Text>
                <Text as="p" variant="bodyMd">
                  {currentPrompt}
                </Text>
              </div>
            </Card>
          </div>
        </Modal.Section>
      </Modal>

      {/* Shopify ResourcePicker */}
      {showResourcePicker && appBridge && (() => {
        const hostValue = sessionStorage.getItem('shopify_host');
        const apiKey = import.meta.env.VITE_SHOPIFY_API_KEY || 'a7cb75270aafc7d0198bb44eec53e8cd';
        
        if (!hostValue) {
          console.error('[ERROR] Cannot open ResourcePicker: no host value available');
          return null;
        }
        
        return (
          <Provider config={{
            apiKey,
            host: hostValue,
            forceRedirect: false
          }}>
            <ResourcePicker
              resourceType={getResourcePickerType(selectedResourceType)}
              showVariants={false}
              allowMultiple={false}
              open={showResourcePicker}
              onSelection={handleResourcePickerSelection}
              onCancel={() => setShowResourcePicker(false)}
            />
          </Provider>
        );
      })()}
    </>
  );
}; 